import { Injectable, inject } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { MeasurePopupComponent } from './components/measure-popup/measure-popup.component';
import { CoreApiService } from './core-api.service';
import { CorePopupService } from './core-popup.service';
import {
  CoreRouterActionNavigateToPageModule,
  CoreRouterActions,
  CoreRouterNavigationIntent
} from './core-router.actions';
import {
  CoreActionLoadMeasure,
  CoreActionNOOP,
  CoreActionOpenExternalApp,
  CoreActions,
  CoreActionsTypes,
  CoreActionTakeMeasure
} from './core.actions';
import { CorePageModuleIDs, ENVIRONMENT, Environment } from './core.models';
import { selectCoreExtraAppConfiguration } from './core.reducer';

@Injectable()
export class CoreEffectsMeasure {
  private store = inject<Store<any>>(Store);
  private actions$ = inject(Actions);
  private platform = inject(Platform);
  private popupService = inject(CorePopupService);
  private coreApiService = inject(CoreApiService);
  private translateService = inject(TranslateService);
  private environment = inject<Environment>(ENVIRONMENT);

  loadMeasure$: Observable<CoreActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.LOAD_MEASURE),
      map((action: CoreActionLoadMeasure) => action.payload),
      switchMap(payload =>
        this.coreApiService.loadMeasure(payload.href).pipe(
          map(
            measure =>
              new CoreActionTakeMeasure({
                measure,
                isBlocking: payload.isBlocking
              })
          ),
          catchError(() => of(new CoreActionNOOP()))
        )
      )
    )
  );

  takeMeasure$: Observable<CoreActions | CoreRouterActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(CoreActionsTypes.TAKE_MEASURE),
      map((action: CoreActionTakeMeasure) => action.payload),
      switchMap(payload => {
        const { measure, isBlocking } = payload;
        const navigate = new CoreRouterActionNavigateToPageModule({
          id: CorePageModuleIDs.TAKE_MEASURE,
          intent: CoreRouterNavigationIntent.FORWARD,
          pageIndex: 1
        });

        const selectorCoreExtraApp = selectCoreExtraAppConfiguration({
          deviceKey: (measure.device && measure.device.name) || '',
          platform: this.platform.is('android') ? 'android' : 'ios',
          subType: measure.subType || '',
          measureUuid: measure.uuid || '',
          devType: this.environment?.devType?.toLowerCase() || void 0,
          bundleId: 'com.bepatient.smartiot',
          bundleURL: this.platform.is('ios')
            ? 'com.bepatient.smartiot://'
            : 'com.bepatient.smartiot'
        });

        return this.store.pipe(
          select(selectorCoreExtraApp),
          take(1),
          switchMap(
            config =>
              new Observable<CoreActions | CoreRouterActions>(subscriber => {
                if (
                  measure.isManualMeasure &&
                  measure.device &&
                  measure.device.useExtra
                ) {
                  this.popupService
                    .create(MeasurePopupComponent, {
                      inputs: {
                        measure,
                        manualLabel: this.translateService.instant(
                          'MEASURE_POPUP.MANUAL_LABEL'
                        )
                      },
                      backdropDismiss: !isBlocking
                    })
                    .then(popup =>
                      popup.takeMeasure.pipe(take(1)).subscribe(type => {
                        subscriber.next(
                          type === 'manual'
                            ? navigate
                            : new CoreActionOpenExternalApp(config)
                        );
                        subscriber.complete();
                        this.popupService.dismiss();
                      })
                    );
                } else if (measure.device && measure.device.useExtra) {
                  subscriber.next(new CoreActionOpenExternalApp(config));
                } else {
                  subscriber.next(navigate);
                }
              })
          )
        );
      })
    )
  );
}
